/**
 * Convert a non-negative INR amount to words (Indian English, title case words).
 * Example: 8485.18 -> "Eight Thousand Four Hundred Eighty Five Rupees Eighteen Paise Only"
 */

const UNITS = [
  "",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
  "Thirteen",
  "Fourteen",
  "Fifteen",
  "Sixteen",
  "Seventeen",
  "Eighteen",
  "Nineteen",
];

const TENS = [
  "",
  "",
  "Twenty",
  "Thirty",
  "Forty",
  "Fifty",
  "Sixty",
  "Seventy",
  "Eighty",
  "Ninety",
];

function twoDigitsToWords(n) {
  if (n < 20) return UNITS[n];
  const t = Math.floor(n / 10);
  const u = n % 10;
  return u ? `${TENS[t]} ${UNITS[u]}` : TENS[t];
}

function belowThousandToWords(n) {
  if (n === 0) return "";
  if (n < 100) return twoDigitsToWords(n);
  const h = Math.floor(n / 100);
  const rest = n % 100;
  const head = `${UNITS[h]} Hundred`;
  if (rest === 0) return head;
  return `${head} ${twoDigitsToWords(rest)}`;
}

/** Indian numbering: Thousand → Lakh → Crore (recursive for any magnitude). */
function integerToWordsIndian(n) {
  if (n === 0) return "Zero";
  if (n < 100) return twoDigitsToWords(n);
  if (n < 1000) return belowThousandToWords(n);
  if (n < 100000) {
    const t = Math.floor(n / 1000);
    const r = n % 1000;
    if (r === 0) return `${twoDigitsToWords(t)} Thousand`;
    return `${twoDigitsToWords(t)} Thousand ${belowThousandToWords(r)}`.trim();
  }
  if (n < 10000000) {
    const l = Math.floor(n / 100000);
    const r = n % 100000;
    if (r === 0) return `${twoDigitsToWords(l)} Lakh`;
    return `${twoDigitsToWords(l)} Lakh ${integerToWordsIndian(r)}`.trim();
  }
  const c = Math.floor(n / 10000000);
  const r = n % 10000000;
  if (r === 0) return `${integerToWordsIndian(c)} Crore`;
  return `${integerToWordsIndian(c)} Crore ${integerToWordsIndian(r)}`.trim();
}

/**
 * @param {number} amount — INR amount (>= 0)
 * @returns {string} e.g. "… Rupees … Paise Only" or "… Rupees Only"
 */
export function amountToWordsInr(amount) {
  if (amount == null || Number.isNaN(Number(amount))) {
    return "Zero Rupees Only";
  }

  const totalPaise = Math.round(Number(amount) * 100);
  const rupees = Math.floor(totalPaise / 100);
  const paise = totalPaise % 100;

  const rupeeWords = integerToWordsIndian(rupees);
  const paiseWords = paise === 0 ? "" : twoDigitsToWords(paise);

  if (paise === 0) {
    return `${rupeeWords} Rupees Only`;
  }
  return `${rupeeWords} Rupees ${paiseWords} Paise Only`;
}
